import React, { useEffect, useState } from "react";
import styles from "./adminSettings.module.css";
import axios from "axios";

const AdminSettings = () => {
  /**
   * This component allows the admin to view and update the website settings. The settings include: the list of supported languages (the admin can add or remove a language), the maximum length (in characters) of a text message, the maxmimum length (in seconds) of a voice message, the maximum number of learning units per learning tool, and the distribution of posts from three groups: Pals' posts, public posts by non-pals, followed users, and public posts by non-pals, non-followed users.
   */

  const apiUrl = process.env.REACT_APP_URL;

  const [languages, setLanguages] = useState([]);
  const [addedLanguage, setAddedLanguage] = useState("");

  const [maxChars, setMaxChars] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState(0);
  const [palsPosts, setPalsPosts] = useState(0);
  const [followedPosts, setFollowedPosts] = useState(0);
  const [otherPosts, setOtherPosts] = useState(0);

  const [maxUnitsPerTool, setMaxUnitsPerTool] = useState(0);

  const [formStatus, setFormStatus] = useState("notSubmitted");

  const [validationMessages, setValidationMessages] = useState({
    maxCharsMsg: "",
    maxSecondsMsg: "",
    palsPostsMsg: "",
    followedPostsMsg: "",
    otherPostsMsg: "",
    totalPostsSumMsg: "",
    maxUnitsPerTool: "",
  });

  useEffect(() => {
    /**
     * Get the website settings from the database
     */
    const fetchData = async () => {
      const settingsRes = await axios.get(`${apiUrl}/admin/settings`, {
        withCredentials: true,
        params: {},
      });

      if (settingsRes.data && settingsRes.data.success) {
        const fetchedSettings = settingsRes.data.settings[0];

        setMaxChars(fetchedSettings.max_text_message_length);
        setMaxSeconds(fetchedSettings.max_voice_message_length);
        setPalsPosts(fetchedSettings.pals_feed_percentage);
        setFollowedPosts(fetchedSettings.followed_feed_percentage);
        setOtherPosts(fetchedSettings.other_feed_percentage);
        setMaxUnitsPerTool(fetchedSettings.max_units_per_tool);
      }

      const languagesRes = await axios.get(`${apiUrl}/search/languages`, {
        withCredentials: true,
      });

      if (languagesRes.data) {
        setLanguages(languagesRes.data.languages);
      }
    };
    fetchData();
  }, []);

  /**
   * Check if a given value is a positive integer
   * @param {*} value - the value to check
   * @returns true if the value is a positive integer, false otherwise
   */
  function isPositiveInteger(value) {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 && !isNaN(number);
  }

  /**
   * Check if a given value is an integer in a given range, between minValue and maxValue (inclusive).
   * @param {*} value - the value to check
   * @param {*} minValue - the minimum possible value
   * @param {*} maxValue - the maximum possible value
   * @returns true if the value is an integer between minValue and maxValue (inclusive), false otherwise
   */
  function isIntegerInRange(value, minValue, maxValue) {
    const number = Number(value);
    return (
      Number.isInteger(number) &&
      number >= minValue &&
      number <= maxValue &&
      !isNaN(number)
    );
  }

  /**
   * Check if a given value is a non-negative integer
   * @param {*} value - the value to check
   * @returns true if the value is a non-negative integer, false otherwise
   */
  function isNonNegativeInteger(value) {
    const number = Number(value);
    return Number.isInteger(number) && number >= 0 && !isNaN(number);
  }

  /**
   * Validate the entered settings and set the validation messages accordingly.
   * @returns true if all settings are valid, false otherwise
   */
  const validateInputs = () => {
    let isValid = true;

    // Validation messages (errors in user inputs)
    const valMessages = {
      maxCharsMsg: "",
      maxSecondsMsg: "",
      palsPostsMsg: "",
      followedPostsMsg: "",
      otherPostsMsg: "",
      totalPostsSumMsg: "",
      maxUnitsPerTool: "",
    };

    if (!isPositiveInteger(maxChars)) {
      isValid = false;
      valMessages.maxCharsMsg = "Must be a positive whole number";
    }

    if (!isPositiveInteger(maxSeconds)) {
      isValid = false;
      valMessages.maxSecondsMsg = "Must be a positive whole number";
    }

    if (!isNonNegativeInteger(palsPosts)) {
      isValid = false;
      valMessages.palsPostsMsg = "Must be a non-negative whole number";
    }

    if (!isNonNegativeInteger(followedPosts)) {
      isValid = false;
      valMessages.followedPostsMsg = "Must be a non-negative whole number";
    }

    if (!isNonNegativeInteger(otherPosts)) {
      isValid = false;
      valMessages.otherPostsMsg = "Must be a non-negative whole number";
    }

    const totalPostsSum =
      parseInt(palsPosts) + parseInt(followedPosts) + parseInt(otherPosts);
    if (totalPostsSum !== 100) {
      isValid = false;
      valMessages.totalPostsSumMsg =
        "The sum of the percentages of Pals, followed, and other users' posts must be exactly 100.";
    } else {
      valMessages.totalPostsSumMsg = "";
    }

    if (!isIntegerInRange(maxUnitsPerTool, 1, 100)) {
      isValid = false;
      valMessages.maxUnitsPerTool = "Must be an integer between 1 and 100.";
    }

    setValidationMessages(valMessages);

    return isValid;
  };

  /**
   * Add a new language to the list of supported languages
   */
  const handleAddNewLanguage = async (e) => {
    e.preventDefault();

    if (!addedLanguage) {
      return;
    }

    const addLanguageRes = await axios.post(
      `${apiUrl}/admin/languages/add`,
      { language: addedLanguage },
      { withCredentials: true }
    );

    if (addLanguageRes.data && addLanguageRes.data.success) {
      setAddedLanguage("");
      await fetchLanguages();
    }
  };

  /**
   * Get the list of supported languages
   */
  const fetchLanguages = async () => {
    const languagesRes = await axios.get(`${apiUrl}/search/languages`, {
      withCredentials: true,
    });

    if (languagesRes.data) {
      setLanguages(languagesRes.data.languages);
    }
  };

  /**
   * Delete a language from the list of supported languages
   */
  const handleDeleteLanguage = async (language) => {
    const deleteLanguageRes = await axios.delete(
      `${apiUrl}/admin/languages/remove`,
      {
        data: { language },
        withCredentials: true,
      }
    );

    if (deleteLanguageRes.data && deleteLanguageRes.data.success) {
      await fetchLanguages();
    }
  };

  /**
   * Update the maximum number of characters in a text message
   */
  const handleChangeMaxChars = (e) => {
    setMaxChars(e.target.value);
  };

  /**
   * Update the maximum number of seconds in a voice message
   */
  const handleChangeMaxSeconds = (e) => {
    setMaxSeconds(e.target.value);
  };

  /**
   * Update the rate of Pals' posts in a user's feed
   */
  const handleChangePalsPostsRate = (e) => {
    setPalsPosts(e.target.value);
  };

  /**
   * Update the rate of public posts by followed, non-Pals users, in a user's feed
   */
  const handleChangeFollowedPostsRate = (e) => {
    setFollowedPosts(e.target.value);
  };

  /**
   * Update the rate of public posts by non-followed, non-Pals users, in a user's feed
   */
  const handleChangeOtherPostsRate = (e) => {
    setOtherPosts(e.target.value);
  };

  /**
   * Update the maximum number of learning units per learning tool.
   */
  const handleMaxUnitsPerTool = (e) => {
    setMaxUnitsPerTool(e.target.value);
  };

  /**
   * Validate the new settings, and if valid - save them in the database.
   */
  const handleSaveSettings = async (e) => {
    e.preventDefault();

    if (validateInputs()) {
      // Save settings to the database

      try {
        const response = await axios.put(
          `${apiUrl}/admin/settings`,
          {
            maxTextMessageLength: maxChars,
            maxVoiceMessageLength: maxSeconds,
            palsFeedPercentage: palsPosts,
            followedFeedPercentage: followedPosts,
            otherFeedPercentage: otherPosts,
            maxUnitsPerTool: maxUnitsPerTool,
          },
          { withCredentials: true }
        );

        if (response.status === 200) {
          setFormStatus("success");
        }
      } catch (e) {
        console.error("Failed to update settings.", e);
        setFormStatus("dbError");
      }
    } else {
      setFormStatus("userError");
    }
  };

  return (
    <div className={styles.adminSettings}>
      <form>
        <p className={styles.formTopic}>Messages</p>
        <div className={styles.formGroup}>
          <div className={styles.inputArea}>
            <label>Maximum number of characters in a text message: </label>

            <input
              type="number"
              value={maxChars}
              onChange={handleChangeMaxChars}
              onBlur={() => validateInputs()}
              min="1"
              required
              className={
                validationMessages.maxCharsMsg ? styles.invalid : styles.valid
              }
            />
          </div>
          {validationMessages.maxCharsMsg && (
            <span className={styles.error}>
              {validationMessages.maxCharsMsg}
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <div className={styles.inputArea}>
            <label>Maximum number of seconds in an audio message: </label>
            <input
              type="number"
              value={maxSeconds}
              onChange={handleChangeMaxSeconds}
              onBlur={() => validateInputs()}
              min="1"
              required
              className={
                validationMessages.maxSecondsMsg ? styles.invalid : styles.valid
              }
            />
          </div>
          {validationMessages.maxSecondsMsg && (
            <span className={styles.error}>
              {validationMessages.maxSecondsMsg}
            </span>
          )}
        </div>

        <p className={styles.formTopic}>User feed components</p>

        <div className={styles.userFeedComponents}>
          <div className={styles.formGroup}>
            <div className={styles.inputArea}>
              <label>Language Pals posts:</label>
              <p>
                <input
                  type="number"
                  value={palsPosts}
                  onChange={handleChangePalsPostsRate}
                  onBlur={() => validateInputs()}
                  min="1"
                  required
                  className={
                    validationMessages.palsPostsMsg
                      ? styles.invalid
                      : styles.valid
                  }
                />
                <span>%</span>
              </p>
            </div>
            {validationMessages.palsPostsMsg && (
              <span className={styles.error}>
                {validationMessages.palsPostsMsg}
              </span>
            )}
          </div>

          <div className={styles.formGroup}>
            <div className={styles.inputArea}>
              <label>Followed users posts:</label>
              <p>
                <input
                  type="number"
                  value={followedPosts}
                  onChange={handleChangeFollowedPostsRate}
                  onBlur={() => validateInputs()}
                  min="1"
                  required
                  className={
                    validationMessages.followedPostsMsg
                      ? styles.invalid
                      : styles.valid
                  }
                />
                <span>%</span>
              </p>
            </div>
            {validationMessages.followedPostsMsg && (
              <span className={styles.error}>
                {validationMessages.followedPostsMsg}
              </span>
            )}
          </div>

          <div className={styles.formGroup + " " + styles.otherPosts}>
            <div className={styles.inputArea}>
              <label>Other users posts:</label>
              <p>
                <input
                  type="number"
                  value={otherPosts}
                  onChange={handleChangeOtherPostsRate}
                  onBlur={() => validateInputs()}
                  min="1"
                  required
                  className={
                    validationMessages.otherPostsMsg
                      ? styles.invalid
                      : styles.valid
                  }
                />
                <span>%</span>
              </p>
            </div>
            {validationMessages.otherPostsMsg && (
              <span className={styles.error}>
                {validationMessages.otherPostsMsg}
              </span>
            )}
          </div>

          <div className={styles.totalSumFeedback}>
            {validationMessages.totalPostsSumMsg && (
              <span className={styles.error}>
                {validationMessages.totalPostsSumMsg}
              </span>
            )}
          </div>
        </div>

        <p className={styles.formTopic}>Learning Zones</p>
        <div className={styles.formGroup}>
          <div className={styles.inputArea}>
            <label>Maximum number of learning units per learning tool</label>
            <p>
              <input
                type="number"
                value={maxUnitsPerTool}
                onChange={handleMaxUnitsPerTool}
                onBlur={() => validateInputs()}
                min="1"
                max="100"
                required
                className={
                  validationMessages.maxUnitsPerTool
                    ? styles.invalid
                    : styles.valid
                }
              />
            </p>
          </div>
          {validationMessages.maxUnitsPerTool && (
            <span className={styles.error}>
              {validationMessages.maxUnitsPerTool}
            </span>
          )}
        </div>

        <div className={styles.languageList}>
          <p className={styles.formTopic}>Supported Languages</p>
          <div>
            <ul>
              {languages.map((languageObj, index) => (
                <li key={index}>
                  {languageObj.language}
                  <span
                    className={styles.deleteIcon}
                    onClick={() => handleDeleteLanguage(languageObj.language)}
                  >
                    X
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.addLanguage}>
            <label>Add a Language:</label>
            <input
              type="text"
              placeholder="Language Name..."
              value={addedLanguage}
              onChange={(e) => setAddedLanguage(e.target.value)}
            />
            <button onClick={handleAddNewLanguage}>Add</button>
          </div>
        </div>

        <div className={styles.btnContainer}>
          <button onClick={handleSaveSettings}>Save Settings</button>
        </div>

        <div className={styles.formFeedback}>
          {formStatus === "success" && (
            <p className={styles.validFeedback}>
              The website settings have been successfully saved.
            </p>
          )}

          {formStatus === "userError" && (
            <p className={styles.invalidFeedback}>
              There was at least one error in the form - please fix the required
              fields and submit the form again.
            </p>
          )}

          {formStatus === "dbError" && (
            <p className={styles.invalidFeedback}>
              There was an error while trying to save the settings. Please try
              again later.
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
